/**
 * Generates HTML email template for "Payment Pre-Authorized".
 * Sent to clients when a pre-authorization hold is placed on their card
 * for an advance / additional-services invoice (funds reserved, not charged).
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter, icon } from '../../emailLayout';
import { trunc2 } from '@/utils';

export const buildInvoicePaymentConfirmationEmailHtml = ({
  invoice = null,
  quote = null,
  cardFee = 0,
  client = null,
  paymentMethod,
  card,
  organization
}) => {
  const services = invoice?.items || [];
  const clientName = `${client?.first_name || ''} ${client?.last_name || ''}`.trim();

  const invoiceSubtotal = parseFloat(invoice?.subtotal) || 0;
  const invoiceTaxAmount = parseFloat(invoice?.tax_amount) || 0;
  const invoicePlatformFee = parseFloat(invoice?.platform_fee) || 0;
  //const invoiceCardFee = parseFloat(invoice?.card_fee) || parseFloat(cardFee) || 0;
  const invoiceTaxesAndFees = trunc2(invoiceTaxAmount + invoicePlatformFee);
  const invoiceTotalAmount = trunc2(invoiceSubtotal + invoiceTaxesAndFees);

  const totalCardFee = trunc2(invoiceTotalAmount * 0.03);
  const totalwithCardFee = trunc2(invoiceTotalAmount + totalCardFee);

  // Default pre-auth hold: 7 days from invoice creation (Visa/MC standard).
  const expiryDate = new Date(new Date(Date.now()).getTime() + 7 * 24 * 60 * 60 * 1000);
    
  const expiryStr = expiryDate.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).replace(/,([^,]*)$/, ' at$1');

  // ── Per-service rows ─────────────────────────────────────────────────────
  const serviceRowsHtml = (() => {
    if (services?.length > 0) {
      return services.map((s, i) => {
        const isLast = i === services.length - 1;
        return `
              <tr>
                <td colspan="2" style="padding:0 20px ${isLast ? '0' : '6px'} 20px;">
                  <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                      <td style="padding:12px 20px;font-size:14px;font-weight:700;color:#111827;background-color:#F7F7F7;">${s.item || s.name || ''}</td>
                      <td align="right" style="padding:12px 20px;font-size:14px;font-weight:700;color:#111827;background-color:#F7F7F7;">$${s.amount}</td>
                    </tr>
                  </table>
                </td>
              </tr>`;
      }).join('');
    }
    return '';
  })();

  // ── Card brand badge ──────────────────────────────────────────────────────
  const cardBrandBadge = card?.brand ? `<span style="display:inline-block;background-color:#252525;color:#FFFFFF;font-size:9px;font-weight:700;padding:2px 5px;border-radius:3px;vertical-align:middle;margin-right:6px;">${card?.brand.toUpperCase()}</span>`
      : '';

  const cardLast4Row = card?.last4
    ? `<td align="right" style="padding:4px 20px 14px;font-size:13px;color:#6B7280;white-space:nowrap;">${cardBrandBadge}•••• •••• •••• ${card?.last4}</td>`
    : '<td></td>';

  const orgStrong = organization?.name
    ? `<strong style="color:#111827;">${organization?.name}</strong>`
    : '';

  return `
${emailDocOpen({ title: 'Payment - Pre-Authorized' })}
${emailHeader({ title: 'Payment Pre-Authorized', organization })}

        ${spacer()}

        <!-- ===== CLIENT / REFERENCE ROW ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice For</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${clientName || 'Valued Client'}</span>
                  </td>
                  <td align="right">
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">TR Number</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${invoice?.quote_number || invoice?.invoice_number}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}


        <!-- ===== NOTIFICATION CARD ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:28px 28px 32px;">
              <!-- Icon -->
              <table role="presentation" cellpadding="0" cellspacing="0" style="margin-bottom:20px;">
                <tr>
                  <td style="width:48px;height:48px;text-align:center;vertical-align:middle;background-color:#FEF3EB;border-radius:100%;">
                    ${icon('bill', 22)}
                  </td>
                </tr>                 
              </table>
              <!-- Body text -->
              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                Thank you for booking with ${orgStrong}. While we secure your requested additional services, a temporary pre-authorization hold of <strong style="color:#111827;">USD ${totalwithCardFee.toFixed(2)}</strong> has been placed on your card${card?.last4 ? ` ending in <strong style="color:#111827;">${card?.last4}</strong>` : ''}.
              </p>

              ${
               paymentMethod=='wire' ? `<p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">The card authorization includes <strong style="color:#111827;">3% credit card fee.</strong> This fee does not apply to your Wire/ACH payment. The total amount you need to send via Wire/ACH is <strong style="color:#111827;">${invoiceTotalAmount.toFixed(2)}</strong></p>` : '' 
              }
               
              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                Please note that your card has not been charged. The funds are being temporarily reserved by your financial institution and will be captured only ${paymentMethod=='wire' ? 'if your preferred payment does not clear and' : ''} once the requested additional services have been confirmed.
              </p> 

              ${paymentMethod === 'card' ? `
              <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
                For further assistance, please reach out to ${orgStrong} or contact your broker directly.
              </p>` : ''}

            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== INVOICE DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td colspan="2" style="padding:14px 20px 8px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice Details</span>
            </td>
          </tr>
          ${serviceRowsHtml}
          <tr><td colspan="2" style="padding:8px 0 14px 0;"></td></tr>
        </table>

        ${spacer()}

        <!-- ===== PRICE BREAKDOWN ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td colspan="2" style="padding:14px 20px 4px;">
              <span style="font-size:14px;font-weight:700;color:#111827;">Price Breakdown</span>
            </td>
          </tr>
          <tr>
            <td colspan="2" style="padding:2px 20px 10px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice</span>
            </td>
          </tr>

          <tr>
            <td style="padding:6px 20px 4px;font-size:13px;color:#6B7280;">Subtotal Cost</td>
            <td align="right" style="padding:6px 20px 4px;font-size:13px;color:#6B7280;">$${invoiceSubtotal.toFixed(2)}</td>
          </tr>
          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Taxes &amp; Fees</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">$${invoiceTaxesAndFees.toFixed(2)}</td>
          </tr>

          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>

          <tr>
            <td style="padding:14px 20px 14px;font-size:15px;font-weight:700;color:#111827;">Total Amount</td>
            <td align="right" style="padding:14px 20px 14px;font-size:18px;font-weight:700;color:#111827;">$${paymentMethod==="card" ? totalwithCardFee.toFixed(2) : invoiceTotalAmount.toFixed(2)}</td>
          </tr> 

          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Credit/Debit Card Fee (3%)</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">$${totalCardFee.toFixed(2)}</td>
          </tr>

          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>
          <tr>
            <td colspan="2" style="padding:10px 20px 4px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Payment Authorization By</span>
            </td>
          </tr>
          <tr>
            <td style="padding:4px 20px 14px;font-size:14px;font-weight:700;color:#111827;">${clientName}</td>
            ${cardLast4Row}
          </tr>

          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>
          <tr>
            <td colspan="2" style="padding:14px 20px 14px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="width:36px;vertical-align:middle;">
                    <table role="presentation" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="width:32px;height:32px;text-align:center;vertical-align:middle;background-color:#F3F4F6;border-radius:100%;">
                          ${icon('calendar', 16)}
                        </td>
                      </tr>
                    </table>
                  </td>
                  <td style="vertical-align:middle;padding-left:10px;">
                    <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Authorization Expiry</div>
                    <div style="font-size:14px;font-weight:700;color:#111827;margin-top:2px;">${expiryStr}</div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>

        ${spacer()}

        ${paymentMethod === 'card' ? `
        <!-- ===== TRIP SUCCEEDED NOTE ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#F3F4F6;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 24px;font-size:13px;color:#6B7280;line-height:1.6;text-align:center;">
              If your trip has succeeded, the authorization will turn into a real charge up to 24 hours before your trip and the amount will be deducted from your payment account.
            </td>
          </tr>
        </table>

        ${spacer()}` : ''}

${emailFooter()}
${emailDocClose()}`;
};
